// import encoding from 'text-encoding'
import moment from 'moment'
import PyRegEx from './pyregex.js'

const decoder = new TextDecoder('latin1')

const DIR = new PyRegEx(/\[DIR(\d+),((\d+)(,\d+)*)\]/g)
const TAG = new PyRegEx(/\[((PACKS = (\d+))|(DATA  )|(STATS )|(TEXT(\d+),(\d+),(\d+))|(SCREEN(\d+),(\d+))|(STRUCTURE)|(ANALOG_LIST)|(DIGITAL_LIST)|(GROUP(\d+),([A-Za-z0-9]+),([A-Za-z0-9]+),(\d+))|(END))\]/g,
  'ALL,PACKS,pkt_cnt,DATA,STATS,TEXT,text_id,text_lines,text_max_length,SCREEN,screen_id,screen_lines,STRUCTURE,ANALOG_LIST,DIGITAL_LIST,GROUP,group_id,group_mux,group_chan,group_lines,END')
const STR = new PyRegEx(/([^\x00]*)\x00/g)
const SIZE = new PyRegEx(/SIZE=(\d+)\x00/g)
const STRUCT = new PyRegEx(/([^\d,\x00]+)(\d+)(us|ss|by|sl|ul|tw)([,\x00])/g,
  'name,quantity,type,term')

class ParseError extends Error {}
class DataError extends Error {}

/**
 * @param {TecuDataPacket} pkt
 * @param {number} n
 */
function make_screen(pkt, n) {
  // Build a string containing the nth screen, using screen 0 as the header
  // and screen n as the body (or '' if there is no screen n).
  let ilines = []
  if (typeof(n) === 'undefined' || n === null) {
    if (pkt.d.screens.length > 1) {
      ilines.push(...pkt.d.screens[1])
      for (let i=2; i<pkt.d.screens.length; i++)
        ilines.push('', ...pkt.d.screens[i])
    }
  } else {
    //ilines.push(...pkt.d.screens[0])
    const [, ...rest] = pkt.d.screens[0] //need to skip title of screen 0 (it's blank anyway)
    ilines.push(...rest)
    if (n && pkt.d.screens.length > n) {
      ilines.push('', ...pkt.d.screens[n])
    }
  }
  let lines = []
  for (let line_in of ilines) {
    let chunks = line_in.split('|')
    switch (chunks.length) {
    case 1:
      lines.push(chunks[0])
      break
    case 2:
      let txt = chunks[0], vars = chunks[1].split(','), line = [], head = 0
      let re = /(@+)(\.@+)?/g
      for (;;) {
        let m = re.exec(txt)
        if (!m) break
        line.push(txt.substring(head, m.index))
        let width = m[0].length,
          decimals = m[2] ? m[2].length - 1 : null
        if (!vars.length) {
          line.push('**NoChannel**')
          head = m.index + m[0].length
          continue
        }
        let v = vars.shift()
        v = /(ts(\d+)([a-zA-Z]+[a-zA-Z\d]+))|(([a-zA-Z]+[a-zA-Z\d]+)([\+\-\*\/\&]\-?\d+)*)$/.exec(v)
        if (v) {
          if (v[1]) {

            // The variable is an index into a list of strings.
            let ts = parseInt(v[2], 10),
              ch = v[3],
              text = pkt.d.texts[ts]
            if (!text) line.push(`**BadList:ts${ts}**`)
            else {
              let index = pkt.get_value(ch)
              if (index === null)
                line.push(`**BadChannel:${ch}**`)
              else if (index < 0 || index >= text.length)
                line.push(`**BadValue:${index}**`)
              else
                line.push(text[index].padEnd(width, ' ').substring(0, width))
            }

          } else {
            let val = null,
              ch = v[5],
              fld = pkt.d.fields[ch]
            if (fld && fld.size > 1) {

              // Analog channel.
              val = pkt.get_value(v[5])
              if (v[6]) {
                // Math!
                let mathre = /([\+\-\*\/\&])(\-)?(\d+)/g
                for (;;) {
                  let exp = mathre.exec(v[6])
                  if (!exp) break
                  let operator = exp[1],
                    negative = exp[2],
                    operand = parseInt(exp[3])
                  if (negative) operand *= -1
                  switch (operator) {
                  case '+': val += operand; break
                  case '-': val -= operand; break
                  case '*': val *= operand; break
                  case '/': val /= operand; break
                  case '&': val &= operand; break
                  }
                }
              }
              if (decimals === null) {
                line.push(
                  String(val)
                    .padStart(width, v[5].substring(0,2) === 'td' ? '0' : ' ')
                )
              } else {
                line.push(
                  (val / (10 ** decimals))
                    .toFixed(decimals)
                    .padStart(width, ' ')
                )
              }

            } else if (ch.length >= 5 && (!fld || fld.size === 1)) {

              // Could be a digital value...
              let chars = ch.substring(2, 4)
              ch = ch.substring(0, 2) + ch.substring(4)
              val = pkt.get_value(ch)
              if (val !== null) {

                // Digital channel.
                line.push(val ? chars[0] : chars[1])

              } else {

                // Could not retrieve channel value.
                line.push(''.padEnd(width, '?'))

              }
            } else {

              // Could not retrieve channel value.
              line.push(''.padEnd(width, '?'))

            }

          }
        }
        head = m.index + m[0].length
      }
      line.push(txt.substring(head))
      lines.push(line.join(''))
      break
    }
  }
  return lines.join('\n')//.trim()
}

export class TecuDataField {
  constructor(offset, size, signed) {
    this.offset = offset
    this.size = size
    this.signed = signed
    this.long_name = null
    this.short_name = null
    this.units = null
    this.channel_min = null
    this.channel_max = null
    this.graph_min = null
    this.graph_max = null
    this.divisor = null
    this.decimals = null
    this.letter = null
  }
}

export class TecuDataPacket {
  constructor (dataset, dataview) {
    this.d = dataset
    this.v = (dataview instanceof DataView) ? dataview : new DataView(dataview)
    this.le = this.v.getUint8(0) !== 0x12
  }
  make_screen(n) { return make_screen(this, n) }
  get localtime() {
    let dt = moment([
      2000 + this.get_value('td0'), // year
      this.get_value('td1') - 1,    // month
      this.get_value('td2'),        // day
      this.get_value('td3'),        // hours
      this.get_value('td4'),        // minutes
      this.get_value('td5'),        // seconds
    ])
    return dt.add(this.get_value('td6') * 100, 'milliseconds')
  }
  get fault_name() { return this.d.texts[1][this.get_value('fc0')].trim() }
  get speed() { return this.get_value(this.d.spd_var) / 100 }
  get gps_coords() {
    if (!this.d.gps_ok_var || !this.get_value(this.d.gps_ok_var))
      return null
    return [this.get_value('gp0', true), this.get_value('gp1', true)]
  }
  get shop_mode() {
    return this.d.shop_mode_var ? this.get_value(this.d.shop_mod_var) : null
  }
  get_text_string(num, index) { return this.d.texts[num][index] }
  get_value(fld, divide) {
    if (typeof(fld) === 'string')
      fld = this.d.fields[fld]
    if (!fld) return null
    if (typeof fld.group_mux !== "undefined" //if this is a grouped value
      && this.get_value(fld.group_mux, true) != fld.group_sel) //and the mux does not select this channel
      return fld.group_value //return the cached value
    let rv, o8 = Math.floor(fld.offset / 8)
    switch (fld.size) {
    case 1:
      // Find bit and convert it to boolean.
      rv = !!(this.v.getUint8(o8) & (1 << (fld.offset % 8)))
      divide = false
      break
    case 8:
      rv = fld.signed ? this.v.getInt8(o8) : this.v.getUint8(o8)
      break
    case 16:
      rv = fld.signed ? this.v.getInt16(o8, this.le)
        : this.v.getUint16(o8, this.le)
      break
    case 32:
      rv = fld.signed ? this.v.getInt32(o8, this.le)
        : this.v.getUint32(o8, this.le)
      break
    default:
      console.error(fld)
      throw new DataError(`unsupported fld type: ${fld}`)
    }
    if (divide && fld.divisor)
      rv /= fld.divisor
    if (typeof fld.group_mux !== "undefined") //if this is a grouped value
      fld.group_value = rv //update the group cache
    return rv
  }
  set_value(fld, value, divide) {
    if (divide)
      throw new Error('set_value(..., divide=true-ish) is not supported.')
    if (typeof(fld) === 'string')
      fld = this.d.fields[fld]
    if (!fld) return
    let rv, o8 = Math.floor(fld.offset / 8)
    switch (fld.size) {
    case 1:
      // Find bit and convert it to boolean.
      if (value)
        this.v.setUint8(o8, this.v.getUint8(o8) | (1 << fld.offset % 8))
      else
        this.v.setUint8(o8, this.v.getUint8(o8) & ~(1 << fld.offset % 8))
      break
    case 8:
      if (fld.signed)
        this.v.setInt8(o8, value)
      else
        this.v.setUint8(o8, value)
      break
    case 16:
      if (fld.signed)
        this.v.setInt16(o8, value, this.le)
      else
        this.v.setUint16(o8, value, this.le)
      break
    case 32:
      if (fld.signed)
        this.v.setInt32(o8, value, this.le)
      else
        this.v.setUint32(o8, value, this.le)
      break
    default:
      console.error(fld)
      throw new DataError(`unsupported fld type: ${fld}`)
    }
  }
  copy() {
    return new TecuDataPacket(this.d, new DataView(this.v.buffer.slice()))
  }
  update_extremes(minpkt, maxpkt) {
    if (this.d !== minpkt.d || this.d !== maxpkt.d)
      throw new Error('Mismatching datasets in packet.update_extremes.')
    for (let key in this.d.fields) {
      let fld = this.d.fields[key],
        v = this.get_value(fld)
      if (minpkt.get_value(fld) > v)
        minpkt.set_value(fld, v)
      if (maxpkt.get_value(fld) < v)
        maxpkt.set_value(fld, v)
    }
  }
}

export class TecuDataView {
  constructor (dataset, start, step, length) {
    this.d = dataset
    this.start = typeof(start) !== 'undefined' ? start : 0
    this.step = typeof(step) !== 'undefined' ? step : 1
    this.length = typeof(length) !== 'undefined' ? length : this.d.length
  }
  normalize_index(i, allow_end) {
    if (i === undefined) return undefined
    if (i < 0) i += this.length
    if (i < 0 || i > this.length || (i === this.length && !allow_end))
      throw new DataError(`list index out of range. ${this.start} ${this.step} ${this.length} ${i}`)
    return i
  }
  get_dataset_index(i) { return this.start + this.step * i }
  getPacket(i) {
    i = this.normalize_index(i)
    i = this.get_dataset_index(i)
    return this.d.getPacket(i)
  }
  slice(start, stop, step) {
    start = this.normalize_index(start, true)
    stop = stop >= this.length ? null : this.normalize_index(stop)
    if (typeof(step) === 'undefined') step = 1
    if (!step) throw new DataError('slice step cannot be zero')
    if (step > 0) {
      if (typeof(start) === 'undefined') start = 0
      if (typeof(stop) === 'undefined')  stop = this.length
      if (start < stop)
        return new TecuDataView(
          this.d,
          this.get_dataset_index(start),
          this.step * step,
          Math.floor((stop - start + step - 1) / step),
        )
    } else {
      if (typeof(start) === 'undefined') start = this.length - 1
      if (typeof(stop) === 'undefined')  stop = -1
      if (start > stop)
        return new TecuDataView(
          this.d,
          this.get_dataset_index(start),
          this.step * step,
          Math.floor((start - stop - step - 1) / -step),
        )
    }
    return new TecuDataView(this.d, 0, 1, 0) // empty view
  }
  * [Symbol.iterator] () {
    let i = this.start, remaining = this.length
    while (remaining > 0) {
      yield this.d.getPacket(i)
      i += this.step
      remaining--
    }
  }
  get texts() { return this.d.texts }
  get screens() { return this.d.screens }
  get fields() { return this.d.fields }
  get fault_text() { return this.d.texts[1] }
  get data_ranges() { return this.d.data_ranges }
  get pkt_size() { return this.d.pkt_size }
}

export class TecuDataSet {
  constructor(buf, str, pos, endpos) {
    this.texts = [[],]
    this.screens = []
    this.pkt_size = null
    this.length = 0
    this.pack_ranges = []
    this.data_ranges = []
    this.gps_ok_var = null
    this.shop_mode_var = null
    this.spd_var = null
    this.buf = buf
    this.fields_by_long_name = {}
    this.fields_by_short_name = {}
    this.faults_by_name = {}
    for (;;) {
      let m = TAG.search(str, pos, endpos)
      if (!m) break
      pos = m.end()
      if (m.group('PACKS')) {
        pos = this.parse_packs(buf, pos, endpos, m.group('pkt_cnt'))
        continue
      } else if (m.group('DATA')) {
        pos = this.parse_data(buf, pos, endpos)
        continue
      } else if (m.group('STATS')) {
        this.parse_stats(buf, pos, endpos)
        continue
      }

      let stop = str.indexOf('[', pos)
      if (stop === -1) stop = endpos

      if (m.group('TEXT'))
        this.parse_text(str, pos, stop,
          m.group('text_id'),
          m.group('text_lines'),
          m.group('text_max_length'))
      else if (m.group('SCREEN'))
        this.parse_screen(str, pos, stop,
          m.group('screen_id'),
          m.group('screen_lines'))
      else if (m.group('STRUCTURE'))
        this.parse_structure(str, pos, stop)
      else if (m.group('ANALOG_LIST'))
        this.parse_analog_list(str, pos, stop)
      else if (m.group('DIGITAL_LIST'))
        this.parse_digital_list(str, pos, stop)
      else if (m.group('GROUP'))
        this.parse_group_list(str, pos, stop,
          m.group('group_id'),
          m.group('group_mux'),
          m.group('group_chan'),
          m.group('group_lines'),
        )
      else if (m.group('END'))
        this.parse_end(str, pos, stop)
    }
  }
  parse_packs(buf, start, stop, pkt_cnt) {
    if (!this.pkt_size)
      throw ParseError('Structure definition must come before packets.')
    pkt_cnt = parseInt(pkt_cnt, 10)
    stop = start + pkt_cnt * this.pkt_size
    this.length += pkt_cnt
    this.pack_ranges.push([start, stop, pkt_cnt])
    return stop
  }
  parse_data(buf, start, stop) {
    this.data_ranges.push([start, stop])
    return stop
  }
  parse_stats(buf, start, stop) {
    return this.parse_packs(buf, start, stop, 1)
  }
  parse_structure(str, start, stop) {
    start++ // skip the first NULL character
    let m = SIZE.match(str, start, stop)
    if (!m) throw new ParseError('Expected SIZE=## tag not found.')
    this.pkt_size = parseInt(m.group(1), 10)
    let offset = 0,
      fields = {},
      item_size,
      signed,
      i
    for (;;) {
      m = STRUCT.match(str, m.end(), stop)
      if (!m) throw new ParseError('Bad structure descriptor.')
      i = m.end()
      let name = m.group('name'),
        quantity = parseInt(m.group('quantity'), 10),
        type = m.group('type')
      switch (type) {
      case 'us': item_size = 16; signed = false; break
      case 'ss': item_size = 16; signed = true; break
      case 'by': item_size = 8; signed = false; break
      case 'ul': item_size = 32; signed = false; break
      case 'sl': item_size = 32; signed = true; break
      case 'tw': item_size = 12; signed = true; break
      default: throw new ParseError(`Unknown field type ${type}`)
      }
      if (name === 'di' || name === 'do') {
        for (let j=0; j<item_size*quantity; j++)
          fields[`${name}${j}`] = new TecuDataField(offset+j, 1, false)
        offset += item_size * quantity
      } else {
        for (let j=0; j<quantity; j++) {
          fields[`${name}${j}`] = new TecuDataField(offset, item_size, signed)
          offset += item_size
        }
      }

      if (m.group('term') === '\x00')
        break
    }
    this.fields = fields
    if (offset != 8 * this.pkt_size)
      throw new ParseError(`Structure size mismatch: ${offset/8} != ${this.pkt_size}`)
    if (i < stop)
      console.log(`STRUCTURE: ${stop-i} unused bytes`)
  }
  parse_text(str, start, stop, index, lines, max_length) {
    // Note: index is discarded. TEXTs must be given in the correct order.
    if (parseInt(index, 10) !== this.texts.length)
      throw new ParseError(`TEXT indices must be in the correct order (${index} !== ${this.texts.length}).`)
    let n, text = []
    start++ // skip the first NULL character
    for (n=0;; n++) {
      let m = STR.search(str, start, stop)
      if (!m) break
      text.push(m.group(1).padEnd(parseInt(max_length), ' '))
      if(index == 1) { //fault text
        //console.log(n, m.group(1))
        this.faults_by_name[m.group(1)] = n
      }
      start = m.end()
    }
    this.texts.push(text)
    if (start < stop)
      console.log(`TEXT: ${stop-start} unused bytes.`)
  }
  parse_screen(str, start, stop, index, lines) {
    let screen = []
    for (;;) {
      let m = STR.search(str, start, stop)
      if (!m) break
      screen.push(m.group(1))
      start = m.end()
    }
    this.screens.push(screen)
    if (start < stop)
      console.log(`SCREEN: ${stop-start} unused bytes.`)
  }
  parse_end(str, start, stop) {
    // do action required
  }
  parse_analog_list(str, start, stop) {
    start++ // skip the first NULL character
    for (;;) {
      let m = STR.search(str, start, stop)
      if (!m) break
      start = m.end()
      let pp = m.group(1).split(',')
      if (pp.length !== 8 && pp.length !== 10) {
        console.log(`Malformed analog descriptor: ${m.group(1)}.`)
        continue
      }
      let channel = pp.shift(),
        fld = this.fields[channel]
      if (!fld) {
        console.log(`Non-existent channel: ${channel}.`)
        continue
      }
      fld.long_name = pp.shift()
      fld.short_name = pp.shift()
      this.fields_by_long_name[fld.long_name] = fld
      this.fields_by_short_name[fld.short_name] = fld
      fld.units = pp.shift()
      if (pp.length == 6) {
        fld.channel_min = parseInt(pp.shift(), 10)
        fld.channel_max = parseInt(pp.shift(), 10)
      }
      fld.graph_min = parseInt(pp.shift(), 10)
      fld.graph_max = parseInt(pp.shift(), 10)
      fld.divisor = parseInt(pp.shift(), 10)
      fld.decimals = parseInt(pp.shift(), 10)
      if (fld.long_name === 'AVG SPD')
        this.spd_var = channel
    }
    if (start < stop)
      console.log(`ANALOG_LIST: ${stop-start} unused bytes.`)
  }
  parse_digital_list(str, start, stop) {
    start++ // skip the first NULL character
    for (;;) {
      let m = STR.search(str, start, stop)
      if (!m) break
      start = m.end()
      let pp = m.group(1).split(',')
      if (pp.length !== 4) {
        console.log(`Malformed digital descriptor: ${m.group(1)}.`)
        continue
      }
      let channel = pp.shift(),
        fld = this.fields[channel]
      if (!fld) {
        console.log(`Non-existent channel: ${channel}.`)
        continue
      }
      fld.long_name = pp.shift()
      fld.short_name = pp.shift()
      this.fields_by_long_name[fld.long_name] = fld
      this.fields_by_short_name[fld.short_name] = fld
      fld.letter = pp.shift()
      switch (fld.long_name) {
      case 'GPS OK': this.gps_ok_var = channel; break
      case 'SHOP MODE': this.shop_mode_var = channel; break
      }
    }
    if (start < stop)
      console.log(`DIGITAL_LIST: ${stop-start} unused bytes.`)
  }
  parse_group_list(str, start, stop, index, group_mux, group_chan, lines) {
    if (typeof this.groups === "undefined") {
      this.groups = []
    }
    let chan = this.fields[group_chan]
    if (!chan) {
      console.log(`Non-existent group channel: ${group_chan}.`)
      return
    }
    let mux = this.fields[group_mux]
    if (!mux) {
      console.log(`Non-existent group multiplex: ${group_mux}.`)
      return
    }
    if (typeof this.groups[index] !== "undefined") {
      console.log(`Duplicate group index: ${index}`)
      return
    }
    else this.groups[index] = []

    let group = this.groups[index]
    group.chan = chan
    group.mux = mux
    group.lines = lines

    start++ // skip the first NULL character
    for (;;) {
      let m = STR.search(str, start, stop)
      if (!m) break
      start = m.end()
      let pp = m.group(1).split(',')
      if (pp.length !== 8 && pp.length !== 10) {
        console.log(`Malformed group descriptor: ${m.group(1)}.`)
        continue
      }

      let channel = pp.shift(),
        fld = new TecuDataField(chan.offset, chan.size, chan.signed)

      fld.group_mux = group_mux //mark as an indirect channel
      fld.group_sel = channel.match(/\d+/)[0] // the mux value that corespondes to this channel
      fld.long_name = pp.shift()
      fld.short_name = pp.shift()
      this.fields_by_long_name[fld.long_name] = fld
      this.fields_by_short_name[fld.short_name] = fld
      fld.units = pp.shift()
      if (pp.length == 6) {
        fld.channel_min = parseInt(pp.shift(), 10)
        fld.channel_max = parseInt(pp.shift(), 10)
      }
      fld.graph_min = parseInt(pp.shift(), 10)
      fld.graph_max = parseInt(pp.shift(), 10)
      fld.divisor = parseInt(pp.shift(), 10)
      fld.decimals = parseInt(pp.shift(), 10)

      group[channel] = fld
    }
    if (start < stop)
      console.log(`DIGITAL_LIST: ${stop-start} unused bytes.`)
  }
  getPacket(i) {
    for (let r of this.pack_ranges) {
      if (i < r[2])
        return new TecuDataPacket(
          this,
          new DataView(this.buf, r[0] + this.pkt_size * i),
        )
      i -= r[2]
    }
    return null
  }
}

export function parseBuffer(buf) {
  let str = decoder.decode(buf)   // Convert to string
  let m = DIR.match(str)          // File split into multiple datasets?
  if (m) {
    // There are multiple sections in this buffer.
    let n = parseInt(m.group(1)), // how many sections?
      pos = m.end(),              // track offset within file
      lengths = m.group(2).split(',').map(len => parseInt(len, 10)),
      datasets = []
    if (n !== lengths.length)
      throw new ParseError(`Bad DIR record (${n} !== ${lengths.length}).`)
    for (let length of lengths) {
      let endpos = pos + length
      datasets.push(new TecuDataView(new TecuDataSet(buf, str, pos, endpos)))
      pos = endpos
    }
    return datasets
  } else {
    // There is only one section in this buffer.
    return [new TecuDataView(new TecuDataSet(buf, str, 0, buf.length))]
  }
}
